import { Banner, Button, Field, Input } from '@cloudflare/kumo';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BRAND_NAME, BRAND_LOGO } from '../lib/brand';
import { TurnstileWidget, useTurnstileSiteKey } from '../components/TurnstileWidget';
import { PasswordField } from '../components/ui/PasswordField';
import { ICONS } from '../components/ui/icons';

const PERKS = [
  '7 dias grátis do plano Profissional, sem compromisso',
  'Agentes de IA no WhatsApp, Instagram e no site, com caixa de entrada',
  'Seus dados ficam guardados quando você assina',
];

// Regras de senha do backend (signupController): 8+ caracteres, maiúscula, minúscula e número
function passwordProblems(s: string): string[] {
  const p: string[] = [];
  if (s.length < 8) p.push('8 caracteres');
  if (!/[A-Z]/.test(s)) p.push('uma maiúscula');
  if (!/[a-z]/.test(s)) p.push('uma minúscula');
  if (!/\d/.test(s)) p.push('um número');
  return p;
}

export function SignupPage() {
  const { startSession } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ nome: '', empresa: '', email: '', whatsapp: '', senha: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const turnstileKey = useTurnstileSiteKey();
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const needCaptcha = !!turnstileKey && !captcha;

  const set = (k: keyof typeof form) => (value: string) => setForm((f) => ({ ...f, [k]: value }));
  const pwProblems = passwordProblems(form.senha);
  const ready = form.nome.trim().length >= 2 && form.empresa.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && pwProblems.length === 0;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true); setError(null); setEmailError(null);
    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, whatsapp: form.whatsapp || undefined, ...(captcha ? { turnstileToken: captcha } : {}) }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (turnstileKey) { setCaptcha(null); setCaptchaReset((n) => n + 1); }
        if (body.code === 'EMAIL_DOMAIN' || body.code === 'EMAIL_IN_USE') setEmailError(body.message);
        else setError(body.message || body.errors?.[0]?.msg || 'Não foi possível criar a conta. Tente de novo.');
        return;
      }
      if (body.mfaRequired) { navigate('/login'); return; }
      await startSession(body.data.token, body.data.user);
      navigate('/dashboard', { replace: true });
    } catch {
      setError('Erro de conexão. Tente de novo.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <img src={BRAND_LOGO} alt={BRAND_NAME} style={{ height: 28, width: 'auto' }} className="[[data-mode=dark]_&]:invert" />
        </div>

        <h2 className="text-xl font-semibold auth-title">Comece seu teste grátis</h2>
        <div style={{ color: 'var(--text-muted)', margin: '6px 0 16px' }}>Crie sua conta em menos de um minuto.</div>

        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', display: 'grid', gap: 8 }}>
          {PERKS.map((p) => (
            <li key={p} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: 'var(--text-2)' }}>
              <ICONS.Checkmark size={16} /> {p}
            </li>
          ))}
        </ul>

        {error && <div className="mb-4"><Banner variant="error" description={<>{error}</>} /></div>}

        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <Field label="Seu nome">
            <Input onChange={(e) => set('nome')(e.target.value)} value={form.nome} autoComplete="name" autoFocus />
          </Field>
          <Field label="Nome da empresa">
            <Input onChange={(e) => set('empresa')(e.target.value)} value={form.empresa} autoComplete="organization" />
          </Field>
          <Field label="E-mail da empresa" error={emailError ?? undefined ? { message: emailError ?? undefined, match: true } : undefined}>
            <Input onChange={(e) => { set('email')(e.target.value); setEmailError(null); }} type="email" value={form.email} autoComplete="email" />
          </Field>
          <Field label="WhatsApp (opcional)">
            <Input onChange={(e) => set('whatsapp')(e.target.value)} type="text" inputMode="tel" placeholder="(63) 99999-9999" value={form.whatsapp} autoComplete="tel" />
          </Field>
          <PasswordField
            label="Senha"
            value={form.senha}
            onChange={set('senha')}
            autoComplete="new-password"
          />
          <div style={{ marginTop: -8, fontSize: 12, color: form.senha && pwProblems.length ? 'var(--crm-text-warning)' : 'var(--text-muted)' }}>
            {form.senha && pwProblems.length ? `Falta: ${pwProblems.join(', ')}` : 'Mínimo de 8 caracteres, com maiúscula, minúscula e número'}
          </div>
          {turnstileKey && <TurnstileWidget siteKey={turnstileKey} action="signup" onToken={setCaptcha} resetKey={captchaReset} />}
          <Button type="submit" variant="primary" loading={busy} disabled={!ready || needCaptcha}>
            Criar conta e começar o teste
          </Button>
        </form>

        <div style={{ marginTop: 20, textAlign: 'center', color: 'var(--text-muted)' }}>
          Já tem conta? <Link to="/login" style={{ color: 'var(--link)', fontWeight: 600 }}>Entrar</Link>
        </div>
      </div>
    </div>
  );
}
